import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineBell } from 'react-icons/hi';
import api from '../../utils/api';

const POLL_INTERVAL_MS = 45000;

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const loadCount = useCallback(() => {
    api.get('/notifications/unread-count')
      .then(({ data }) => setUnread(data.unreadCount || 0))
      .catch(() => {});
  }, []);

  const loadList = useCallback(() => {
    setLoading(true);
    api.get('/notifications', { params: { limit: 20 } })
      .then(({ data }) => {
        setItems(data.notifications || []);
        setUnread(data.unreadCount || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCount();
    pollRef.current = setInterval(loadCount, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [loadCount]);

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const handleClick = async (n) => {
    if (!n.isRead) {
      setItems(list => list.map(x => (x.id === n.id ? { ...x, isRead: true } : x)));
      setUnread(u => Math.max(0, u - 1));
      api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    setOpen(false);
    if (n.linkPath) navigate(n.linkPath);
  };

  const markAllRead = async () => {
    setItems(list => list.map(x => ({ ...x, isRead: true })));
    setUnread(0);
    api.post('/notifications/read-all').catch(() => {});
  };

  return (
    <div className="relative">
      <button
        onClick={toggleOpen}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        <HiOutlineBell className="w-6 h-6 text-gray-600" />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-h-[26rem] overflow-auto bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 sticky top-0 bg-white">
              <span className="font-semibold text-sm text-gray-900">Notifications</span>
              {unread > 0 && (
                <button onClick={markAllRead} className="text-xs text-v2-blue hover:underline">
                  Mark all read
                </button>
              )}
            </div>

            {loading ? (
              <div className="py-8 flex justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-v2-blue" />
              </div>
            ) : items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-400">No notifications yet</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {items.map(n => (
                  <li key={n.id}>
                    <button
                      onClick={() => handleClick(n)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${n.isRead ? '' : 'bg-blue-50/60'}`}
                    >
                      <div className="flex items-start gap-2">
                        <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${n.isRead ? 'bg-transparent' : 'bg-v2-blue'}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">{n.title}</p>
                          {n.body && <p className="text-xs text-gray-500 mt-0.5 break-words">{n.body}</p>}
                          <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.createdAt)}</p>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
